import { collection, doc, setDoc, getDocs, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface CachedEmail {
  id: string;
  subject: string;
  from: string;
  to: string;
  date: Date;
  content: string;
  htmlContent?: string;
  isHtml: boolean;
  isSent: boolean;
  direction: 'sent' | 'received';
  threadId: string;
  userId: string;
  syncedAt: Date;
}

export const emailCacheService = {
  /**
   * Save emails to Firestore cache
   */
  async cacheEmails(userId: string, emails: any[]): Promise<void> {
    try {
      const batch: Promise<void>[] = [];
      
      for (const email of emails) {
        const emailDoc = {
          ...email,
          date: Timestamp.fromDate(new Date(email.date)),
          syncedAt: Timestamp.now(),
          userId
        };
        
        const docRef = doc(db, 'cached_emails', `${userId}_${email.id}`);
        batch.push(setDoc(docRef, emailDoc, { merge: true }));
      }

      await Promise.all(batch);
      console.log(`Cached ${emails.length} emails to Firestore`);
    } catch (error) {
      console.error('Error caching emails:', error);
      throw error;
    }
  },

  /**
   * Get cached emails from Firestore
   */
  async getCachedEmails(userId: string): Promise<CachedEmail[]> {
    try {
      const q = query(
        collection(db, 'cached_emails'),
        where('userId', '==', userId),
        orderBy('date', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const emails = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          date: data.date?.toDate() || new Date(),
          syncedAt: data.syncedAt?.toDate() || new Date()
        } as CachedEmail;
      });

      console.log(`Retrieved ${emails.length} cached emails from Firestore`);
      return emails;
    } catch (error) {
      console.error('Error getting cached emails:', error);
      return [];
    }
  },

  /**
   * Get last sync date
   */
  async getLastSyncDate(userId: string): Promise<Date | null> {
    try {
      const q = query(
        collection(db, 'cached_emails'),
        where('userId', '==', userId),
        orderBy('date', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const latestEmail = snapshot.docs[0].data();
      return latestEmail.date?.toDate() || null;
    } catch (error) {
      console.error('Error getting last sync date:', error);
      return null;
    }
  },

  /**
   * Sync new emails (incremental)
   */
  async syncNewEmails(userId: string, newEmails: any[]): Promise<void> {
    // Get existing email IDs to avoid duplicates
    const cached = await this.getCachedEmails(userId);
    const existingIds = new Set(cached.map(e => e.id));

    // Filter only new emails
    const uniqueNewEmails = newEmails.filter(email => !existingIds.has(email.id));

    if (uniqueNewEmails.length > 0) {
      await this.cacheEmails(userId, uniqueNewEmails);
      console.log(`Synced ${uniqueNewEmails.length} new emails (${newEmails.length - uniqueNewEmails.length} duplicates skipped)`);
    } else {
      console.log('No new emails to sync');
    }
  }
};

